import React, { useState, useEffect } from 'react';
import { useAuth, createApiClient } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Globe,
  Flame,
  Trophy,
  Compass,
  MapTrifold,
  Star,
  Wine,
  Sparkle,
  Camera,
  PencilSimple,
  Heart,
  Check,
  Lightning,
  Target,
  SpinnerGap
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const ICON_MAP = {
  globe: Globe,
  flame: Flame,
  trophy: Trophy,
  compass: Compass,
  map: MapTrifold,
  star: Star,
  wine: Wine,
  sparkle: Sparkle,
  camera: Camera,
  pencil: PencilSimple,
  heart: Heart,
};

const CATEGORY_LABELS = {
  all: 'All',
  exploration: 'Exploration',
  streak: 'Streaks',
  discovery: 'Discovery',
  awards: 'Awards',
  milestone: 'Milestones',
  engagement: 'Engagement',
};

const DIFFICULTY_STYLES = {
  easy: 'bg-[#D1E2C4] text-[#3E4A39]',
  medium: 'bg-[#E8DCD0] text-[#8B6F47]',
  hard: 'bg-[#A94438]/15 text-[#A94438]',
};

const Challenges = () => {
  const { token } = useAuth();
  const api = createApiClient(token);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchChallenges();
  }, []);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      const res = await api.get('/challenges');
      setData(res.data);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      toast.error('Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (challengeId) => {
    setAccepting(challengeId);
    try {
      await api.post(`/challenges/${challengeId}/accept`);
      toast.success('Challenge accepted!');
      fetchChallenges();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to accept challenge';
      toast.error(msg);
    } finally {
      setAccepting(null);
    }
  };

  const filtered = data?.challenges?.filter(
    (c) => activeCategory === 'all' || c.category === activeCategory
  ) || [];

  const categories = ['all', ...new Set((data?.challenges || []).map((c) => c.category))];

  if (loading) {
    return (
      <div className="container-app py-8 pb-8">
        <div className="flex items-center justify-center py-32">
          <SpinnerGap className="w-8 h-8 animate-spin text-[#A94438]" />
        </div>
      </div>
    );
  }

  const streak = data?.streak || {};

  return (
    <div className="container-app py-8 pb-8" data-testid="challenges-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1A1918]" data-testid="challenges-title">
          Dining Challenges
        </h1>
        <p className="text-[#7A7873] mt-1">
          Push your palate — accept challenges and build streaks
        </p>
      </div>

      {/* Streak Banner */}
      <div
        className="rounded-2xl border border-[#EAE8E3] bg-white p-5 sm:p-6 mb-8 grid grid-cols-2 sm:grid-cols-4 gap-4"
        data-testid="streak-banner"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A94438] to-[#C4A35A] flex items-center justify-center">
            <Flame weight="fill" className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-[#1A1918]" data-testid="current-streak">
              {streak.current_streak || 0}
            </p>
            <p className="text-xs text-[#7A7873]">Week streak</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C4A35A]/15 flex items-center justify-center">
            <Trophy weight="duotone" className="w-6 h-6 text-[#C4A35A]" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-[#1A1918]" data-testid="longest-streak">
              {streak.longest_streak || 0}
            </p>
            <p className="text-xs text-[#7A7873]">Best streak</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#4B5945]/15 flex items-center justify-center">
            <Target weight="duotone" className="w-6 h-6 text-[#4B5945]" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-[#1A1918]" data-testid="total-completed">
              {data?.total_completed || 0}
            </p>
            <p className="text-xs text-[#7A7873]">Completed</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#A94438]/10 flex items-center justify-center">
            <Lightning weight="duotone" className="w-6 h-6 text-[#A94438]" />
          </div>
          <div>
            <p className="text-2xl font-heading font-bold text-[#1A1918]" data-testid="total-visits">
              {streak.total_visits || 0}
            </p>
            <p className="text-xs text-[#7A7873]">Total visits</p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 flex-wrap mb-6" data-testid="category-filters">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`filter-pill ${activeCategory === cat ? 'active' : ''}`}
            data-testid={`filter-${cat}`}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      {/* Challenge Cards */}
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
        }}
      >
        <AnimatePresence mode="popLayout">
          {filtered.map((challenge) => {
            const Icon = ICON_MAP[challenge.icon] || Star;
            const isComplete = challenge.completed;
            const isAccepted = challenge.accepted;

            return (
              <motion.div
                key={challenge.id}
                layout
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: { opacity: 1, y: 0 },
                }}
                exit={{ opacity: 0, scale: 0.95 }}
                className={`rounded-2xl border bg-white overflow-hidden transition-shadow hover:shadow-md ${
                  isComplete
                    ? 'border-[#4B5945]/30 bg-[#D1E2C4]/5'
                    : 'border-[#EAE8E3]'
                }`}
                data-testid={`challenge-card-${challenge.id}`}
              >
                <div className="p-5">
                  {/* Top row: icon + difficulty */}
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isComplete
                          ? 'bg-[#4B5945] text-white'
                          : 'bg-[#F9F8F5] text-[#A94438]'
                      }`}
                    >
                      {isComplete ? (
                        <Check weight="bold" className="w-5 h-5" />
                      ) : (
                        <Icon weight="duotone" className="w-5 h-5" />
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="secondary"
                        className={`text-[10px] uppercase tracking-wider font-semibold ${
                          DIFFICULTY_STYLES[challenge.difficulty] || ''
                        }`}
                        data-testid={`difficulty-${challenge.id}`}
                      >
                        {challenge.difficulty}
                      </Badge>
                      {challenge.duration !== 'ongoing' && (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-[#7A7873]">
                          {challenge.duration}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Title + description */}
                  <h3 className="font-heading font-semibold text-lg text-[#1A1918] mb-1">
                    {challenge.title}
                  </h3>
                  <p className="text-sm text-[#7A7873] mb-4 leading-relaxed">
                    {challenge.description}
                  </p>

                  {/* Progress bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-[#7A7873]">Progress</span>
                      <span className="font-medium text-[#1A1918]" data-testid={`progress-${challenge.id}`}>
                        {challenge.progress}/{challenge.target}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-[#EAE8E3] overflow-hidden">
                      <motion.div
                        className={`h-full rounded-full ${
                          isComplete
                            ? 'bg-[#4B5945]'
                            : 'bg-gradient-to-r from-[#A94438] to-[#C4A35A]'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${challenge.percentage}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    </div>
                  </div>

                  {/* Action */}
                  {isComplete ? (
                    <div className="flex items-center gap-2 text-[#4B5945] text-sm font-medium" data-testid={`completed-${challenge.id}`}>
                      <Check weight="bold" className="w-4 h-4" />
                      Completed
                    </div>
                  ) : isAccepted ? (
                    <div className="text-xs text-[#7A7873]" data-testid={`accepted-${challenge.id}`}>
                      Accepted — keep going!
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleAccept(challenge.id)}
                      disabled={accepting === challenge.id}
                      className="w-full bg-[#A94438] hover:bg-[#8B362C] text-white rounded-xl"
                      data-testid={`accept-btn-${challenge.id}`}
                    >
                      {accepting === challenge.id ? (
                        <SpinnerGap className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Lightning weight="bold" className="w-4 h-4 mr-1" />
                      )}
                      Accept Challenge
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {filtered.length === 0 && (
        <div className="empty-state" data-testid="no-challenges">
          <Trophy weight="duotone" className="empty-state-icon" />
          <h3 className="empty-state-title">No challenges in this category</h3>
          <p className="empty-state-text">Try selecting a different filter above</p>
        </div>
      )}
    </div>
  );
};

export default Challenges;
